import React, { useState, useEffect } from 'react';
import Select2Dropdown from '../common/Select2Dropdown';
import { toast } from 'react-toastify';
import { usePermissions } from '../../services/PermissionsContext';
import { openFilePreview } from '../common/FilePreviewModal';

const STAFF_API = '/api/admin/staff/';

const formatMoney = (value) => {
    const num = Number(String(value ?? '').replace(/,/g, ''));
    if (Number.isNaN(num)) return '0.00';
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const normalizeMoneyInput = (value) => {
    const cleaned = String(value ?? '').replace(/,/g, '').replace(/[^\d.]/g, '');
    const parts = cleaned.split('.');
    if (parts.length <= 1) return cleaned;
    return `${parts[0]}.${parts.slice(1).join('')}`;
};

const stripMoneyFormatting = (value) => String(value ?? '').replace(/,/g, '');

const authHeaders = (json = true) => {
    const token = localStorage.getItem('kk_token') || localStorage.getItem('token');
    return {
        ...(json ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {})
    };
};

const RELIGION_OPTIONS = [
    { id: 'CHRISTIAN', text: 'CHRISTIAN' },
    { id: 'HINDU', text: 'HINDU' },
    { id: 'JEW', text: 'JEW' },
    { id: 'MUSLIM', text: 'MUSLIM' },
    { id: 'OTHER', text: 'OTHER' }
];

const MARITAL_STATUS_OPTIONS = [
    { id: 'MARRIED', text: 'MARRIED' },
    { id: 'DIVORCED', text: 'DIVORCED' },
    { id: 'SINGLE', text: 'SINGLE' },
    { id: 'WIDOWED', text: 'WIDOWED' }
];

const FAMILY_RELATION_OPTIONS = [
    { id: 'SPOUSE', text: 'SPOUSE' },
    { id: 'SON', text: 'SON' },
    { id: 'DAUGHTER', text: 'DAUGHTER' },
    { id: 'FATHER', text: 'FATHER' },
    { id: 'MOTHER', text: 'MOTHER' }
];

const StaffForm = ({ mode, initialData, departments, onClose }) => {
    const { hasPermission } = usePermissions();
    const [saving, setSaving] = useState(false);
    const [readOnly, setReadOnly] = useState(mode === 'view');
    const [nidaError, setNidaError] = useState('');

    const getFileName = (fileOrUrl) => {
        if (!fileOrUrl) return null;
        if (fileOrUrl instanceof File) {
            return fileOrUrl.name;
        }
        if (typeof fileOrUrl === 'string') {
            return fileOrUrl.split('/').pop();
        }
        return null;
    };
    
    const [form, setForm] = useState({
        id: null,
        first_name: '',
        middle_name: '',
        last_name: '',
        department: '',
        email: '',
        office_email: '',
        phone: '',
        position: '',
        address: '',
        joining_date: '',
        dob: '',
        nssf: '',
        active: true,
        nida: '',
        tin: '',
        gender: 'MALE',
        religion: '',
        marital_status: '',
        short_name: '',
        email_signature: '',
        signature_image: null,
        signature_image_preview: null,
        staff_sign: null,
        staff_sign_preview: null,
        photo: null,
        photo_preview: null,
        phone_verified: false
    });

    const [contracts, setContracts] = useState([]);
    const [documents, setDocuments] = useState([]);
    const [guarantors, setGuarantors] = useState([]);
    const [familyMembers, setFamilyMembers] = useState([]);

    useEffect(() => {
        if (initialData) {
            setForm({
                id: initialData.id,
                first_name: initialData.first_name || '',
                middle_name: initialData.middle_name || '',
                last_name: initialData.last_name || '',
                department: initialData.department || '',
                email: initialData.email || '',
                office_email: initialData.office_email || '',
                phone: initialData.phone || '',
                position: initialData.position || '',
                address: initialData.address || '',
                joining_date: initialData.joining_date || '',
                dob: initialData.dob || '',
                nssf: initialData.nssf || '',
                active: initialData.active,
                nida: initialData.nida || '',
                tin: initialData.tin || '',
                gender: initialData.gender || 'MALE',
                religion: initialData.religion || '',
                marital_status: initialData.marital_status || '',
                short_name: initialData.short_name || '',
                email_signature: initialData.email_signature || '',
                signature_image: null,
                signature_image_preview: initialData.signature_image || null,
                staff_sign: null,
                staff_sign_preview: initialData.staff_sign || null,
                photo: null,
                photo_preview: initialData.photo || null,
                phone_verified: initialData.phone_verified || false
            });
            setContracts((initialData.contracts || []).map(contract => ({
                ...contract,
                gross_salary: formatMoney(contract.gross_salary || 0),
                others: formatMoney(contract.others || 0),
                allowance: formatMoney(contract.allowance || 0),
            })));
            setDocuments(initialData.documents || []);
            setGuarantors(initialData.guarantors || []);
            
            const processedFamilyMembers = (initialData.family_members || []).map(fm => {
                let isPhoneNa = false;
                if (fm.dob) {
                    const dob = new Date(fm.dob);
                    const today = new Date();
                    let age = today.getFullYear() - dob.getFullYear();
                    const m = today.getMonth() - dob.getMonth();
                    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
                        age--;
                    }
                    // If under 18 and phone is empty, assume N/A was selected
                    if (age < 18 && !fm.phone) {
                        isPhoneNa = true;
                    }
                }
                return { ...fm, phone_na: isPhoneNa };
            });
            setFamilyMembers(processedFamilyMembers);
        }
        setReadOnly(mode === 'view');
    }, [initialData, mode]);

    const handleInputChange = (e, field) => {
        const value = e.target.value.toUpperCase();
        setForm(f => ({ ...f, [field]: value }));
    };

    const handleEmailChange = (e) => {
        setForm(f => ({ ...f, email: e.target.value }));
    };

    const handlePhotoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setForm(f => ({
                ...f,
                photo: file,
                photo_preview: URL.createObjectURL(file)
            }));
        }
    };

    const handleViewFile = (file, fileName = 'Document') => {
        if (file instanceof File) {
            const fileUrl = URL.createObjectURL(file);
            openFilePreview(fileUrl, file.name || fileName);
        } else if (typeof file === 'string') {
            openFilePreview(file, fileName);
        }
    };

    // --- Contracts ---
    const addContract = () => {
        setContracts([...contracts, { start_date: '', end_date: '', gross_salary: '0.00', others: '0.00', allowance: '0.00', no_nssf: false, no_nhif: false, document: null }]);
    };

    const removeContract = (index) => {
        const newContracts = [...contracts];
        newContracts.splice(index, 1);
        setContracts(newContracts);
    };

    const updateContract = (index, field, value) => {
        const newContracts = [...contracts];
        newContracts[index][field] = value;
        setContracts(newContracts);
    };

    const handleContractMoneyChange = (index, field, value) => {
        updateContract(index, field, normalizeMoneyInput(value));
    };

    const handleContractMoneyFocus = (index, field, value) => {
        updateContract(index, field, stripMoneyFormatting(value));
    };

    const handleContractMoneyBlur = (index, field, value) => {
        updateContract(index, field, formatMoney(value || 0));
    };

    const handleContractFileChange = (index, e) => {
        const file = e.target.files[0];
        if (file) {
            const newContracts = [...contracts];
            newContracts[index].document = file;
            setContracts(newContracts);
        }
    };

    const sortedContractsWithIndex = contracts
        .map((contract, originalIndex) => ({ contract, originalIndex }))
        .sort((a, b) => {
            const aEnd = a.contract.end_date ? new Date(a.contract.end_date).getTime() : Number.POSITIVE_INFINITY;
            const bEnd = b.contract.end_date ? new Date(b.contract.end_date).getTime() : Number.POSITIVE_INFINITY;
            return aEnd - bEnd;
        });

    // --- Documents ---
    const addDocument = () => {
        setDocuments([...documents, { name: '', file: null }]);
    };

    const removeDocument = (index) => {
        const newDocs = [...documents];
        newDocs.splice(index, 1);
        setDocuments(newDocs);
    };

    const updateDocument = (index, field, value) => {
        const newDocs = [...documents];
        newDocs[index][field] = typeof value === 'string' ? value.toUpperCase() : value;
        setDocuments(newDocs);
    };

    const handleDocumentFileChange = (index, e) => {
        const file = e.target.files[0];
        if (file) {
            const newDocs = [...documents];
            newDocs[index].file = file;
            setDocuments(newDocs);
        }
    };

    // --- Guarantors ---
    const addGuarantor = () => {
        setGuarantors([...guarantors, { 
            first_name: '', middle_name: '', last_name: '', gender: 'MALE',
            phone: '', phone_verified: false, nida: '', 
            relationship: '', place_of_work: '', 
            photo: null, nida_copy: null, local_government_letter: null
        }]);
    };

    const removeGuarantor = (index) => {
        const newGuarantors = [...guarantors];
        newGuarantors.splice(index, 1);
        setGuarantors(newGuarantors);
    };

    const updateGuarantor = (index, field, value) => {
        const newGuarantors = [...guarantors];
        
        if (field === 'nida') {
            newGuarantors[index][field] = formatNidaString(value);
            newGuarantors[index].nida_error = ''; // Clear error on change
        } else if (['first_name', 'middle_name', 'last_name', 'relationship', 'place_of_work'].includes(field)) {
            newGuarantors[index][field] = value.toUpperCase();
        } else {
            newGuarantors[index][field] = value;
        }
        setGuarantors(newGuarantors);
    };

    const handleGuarantorFileChange = (index, field, e) => {
        const file = e.target.files[0];
        if (file) {
            const newGuarantors = [...guarantors];
            newGuarantors[index][field] = file;
            setGuarantors(newGuarantors);
        }
    };

    // --- Family Members ---
    const addFamilyMember = () => {
        if (familyMembers.length >= 10) {
            toast.warning('Maximum 10 family members allowed.');
            return;
        }
        setFamilyMembers([...familyMembers, {
            first_name: '', middle_name: '', last_name: '',
            relation: '', dob: '', phone: '', address: ''
        }]);
    };

    const removeFamilyMember = (index) => {
        const newMembers = [...familyMembers];
        newMembers.splice(index, 1);
        setFamilyMembers(newMembers);
    };

    const updateFamilyMember = (index, field, value) => {
        const newMembers = [...familyMembers];
        if (['first_name', 'middle_name', 'last_name', 'address'].includes(field)) {
            newMembers[index][field] = value.toUpperCase();
        } else {
            newMembers[index][field] = value;
        }
        setFamilyMembers(newMembers);
    };

    // --- Validation Helpers ---
    const validateNIDA = (nida) => {
        const regex = /^\d{8}-\d{5}-\d{5}-\d{2}$/;
        return regex.test(nida);
    };

    const validateTIN = (tin) => {
        const regex = /^\d{3}-\d{3}-\d{3}$/;
        return regex.test(tin);
    };

    const validatePhone = (phone) => {
        // Remove non-digit characters to count digits
        const digits = phone.replace(/\D/g, '');
        const len = digits.length;
        return len === 10 || len === 12;
    };

    const checkNidaAvailability = async (nida, type = 'staff', index = null) => {
        if (!nida || nida.length < 20) return; // Wait for full NIDA (basic length check)
        
        // Clear previous error before checking
        if (type === 'staff') setNidaError('');
        else if (type === 'guarantor' && index !== null) {
            const newGuarantors = [...guarantors];
            if (newGuarantors[index]) {
                newGuarantors[index].nida_error = '';
                setGuarantors(newGuarantors);
            }
        }

        try {
            let url = `${STAFF_API}check_nida/?nida=${nida}&type=${type}`;
            if (type === 'staff' && form.id) {
                url += `&exclude_staff_id=${form.id}`;
            }
            
            const res = await fetch(url, { headers: authHeaders() });
            const data = await res.json();
            
            if (!data.available) {
                if (type === 'staff') {
                   setForm(f => ({ ...f, nida: '' })); // Clear field
                   setNidaError(data.message);
                } else if (type === 'guarantor' && index !== null) {
                   const newGuarantors = [...guarantors];
                   if (newGuarantors[index]) {
                        newGuarantors[index].nida = ''; // Clear field
                        newGuarantors[index].nida_error = data.message;
                        setGuarantors(newGuarantors);
                   }
                }
            }
        } catch (err) {
            console.error("Error checking NIDA:", err);
        }
    };

    const formatNidaString = (val) => {
        const raw = val.replace(/\D/g, '');
        let formatted = '';
        // yyyymmdd
        if (raw.length > 0) formatted += raw.substring(0, 8);
        // -xxxxx
        if (raw.length > 8) formatted += '-' + raw.substring(8, 13);
        // -xxxxx
        if (raw.length > 13) formatted += '-' + raw.substring(13, 18);
        // -xx
        if (raw.length > 18) formatted += '-' + raw.substring(18, 20);
        return formatted;
    };

    const handleDobChange = (e) => {
        const val = e.target.value;
        setForm(f => {
            let nida = f.nida;
            if (val) {
                const prefix = val.replaceAll('-', '');
                const parts = nida.split('-');
                let suffix = '';
                if (parts.length > 1) {
                    suffix = nida.substring(nida.indexOf('-')); 
                }
                if (!suffix) suffix = '-';
                nida = prefix + suffix;
            }
            return { ...f, dob: val, nida };
        });
    };

    const handleNidaChange = (e) => {
        setForm(f => ({ ...f, nida: formatNidaString(e.target.value) }));
        if (nidaError) setNidaError('');
    };

    const handleTinChange = (e) => {
        const raw = e.target.value.replace(/\D/g, '');
        let formatted = '';
        // xxx
        if (raw.length > 0) formatted += raw.substring(0, 3);
        // -xxx
        if (raw.length > 3) formatted += '-' + raw.substring(3, 6);
        // -xxx
        if (raw.length > 6) formatted += '-' + raw.substring(6, 9);
        
        setForm(f => ({ ...f, tin: formatted }));
    };

    const handleNssfChange = (e) => {
        const val = e.target.value.replace(/\D/g, '');
        setForm(f => ({ ...f, nssf: val }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim() || 
            !form.middle_name.trim() || !form.department || !form.phone.trim() || 
            !form.position.trim() || !form.address.trim() || !form.dob || 
            !form.joining_date || !form.nida.trim() || !form.tin.trim() || !form.nssf.trim() ||
            !form.religion || !form.marital_status) {
            toast.error('All fields in Basic Information are required.');
            return;
        }

        if (!validatePhone(form.phone)) {
            toast.error('Phone number must be exactly 10 or 12 digits.');
            return;
        }

        if (form.nida && !validateNIDA(form.nida)) {
            toast.error('Invalid NIDA format. Expected 20 digits: yyyymmdd-xxxxx-xxxxx-xx');
            return;
        }

        if (form.tin && !validateTIN(form.tin)) {
            toast.error('Invalid TIN format. Expected 9 digits: xxx-xxx-xxx');
            return;
        }

        for (const contract of contracts) {
            if (!contract.start_date || !contract.end_date || !contract.gross_salary) {
                toast.error('All fields in Contracts are required.');
                return;
            }
            if (!contract.id && !contract.document) {
                 toast.error('Contract document is required for new contracts.');
                 return;
            }
        }

        if (guarantors.length < 2) {
            toast.error('At least 2 guarantors are required.');
            return;
        }

        for (const g of guarantors) {
            if (!g.first_name || !g.middle_name || !g.last_name || !g.phone || !g.nida || !g.relationship || !g.place_of_work || !g.gender) {
                toast.error('All fields in Guarantors are required.');
                return;
            }

            if (!validatePhone(g.phone)) {
                toast.error(`Guarantor ${g.first_name}'s phone number must be exactly 10 or 12 digits.`);
                return;
            }

            if (!validateNIDA(g.nida)) {
                toast.error(`Guarantor ${g.first_name}'s NIDA is invalid. Expected 20 digits: yyyymmdd-xxxxx-xxxxx-xx`);
                return;
            }

             if (!g.local_government_letter) {
                 toast.error(`Local Government Letter is required for guarantor ${g.first_name}.`);
                 return;
             }

             if (!g.guarantor_letter) {
                 toast.error(`Guarantor Letter is required for guarantor ${g.first_name}.`);
                 return;
             }

             if (!g.id && (!g.photo || !g.nida_copy)) {
                 toast.error('Photo and NIDA Copy are required for new guarantors.');
                 return;
            }
        }

        for (const fm of familyMembers) {
            if (!fm.first_name || !fm.last_name || !fm.relation || !fm.dob) {
                toast.error('Name, Relation and DOB are required for Family Members.');
                return;
            }

            // Age Calculation
            const dob = new Date(fm.dob);
            const today = new Date();
            let age = today.getFullYear() - dob.getFullYear();
            const m = today.getMonth() - dob.getMonth();
            if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
                age--;
            }

            if (age >= 18) {
                 if (!fm.phone || !validatePhone(fm.phone)) {
                    toast.error(`Family member ${fm.first_name}'s phone number is required and must be valid (Age >= 18).`);
                    return;
                 }
            } else {
                // Age < 18
                if (!fm.phone_na && (!fm.phone || !validatePhone(fm.phone))) {
                     toast.error(`Family member ${fm.first_name}'s phone number is required unless N/A is checked.`);
                     return;
                }
            }
        }

        for (const doc of documents) {
            if (!doc.name) {
                toast.error('Document Name is required.');
                return;
            }
            if (!doc.id && !doc.file) {
                toast.error('File is required for new documents.');
                return;
            }
        }

        const canSave = form.id ? hasPermission('STAFFS', 'can_edit') : hasPermission('STAFFS', 'can_create');
        if (!canSave) {
            toast.error("You do not have permission to save this staff member.");
            return;
        }

        setSaving(true);
        try {
            const formData = new FormData();
            formData.append('first_name', form.first_name.trim());
            formData.append('middle_name', form.middle_name.trim());
            formData.append('last_name', form.last_name.trim());
            formData.append('email', form.email.trim());
            formData.append('office_email', (form.office_email || '').trim());
            if (form.department) formData.append('department', form.department);
            if (form.phone) formData.append('phone', form.phone);
            if (form.position) formData.append('position', form.position);
            if (form.address) formData.append('address', form.address);
            if (form.joining_date) formData.append('joining_date', form.joining_date);
            if (form.dob) formData.append('dob', form.dob);
            if (form.nssf) formData.append('nssf', form.nssf);
            formData.append('active', form.active);
            if (form.nida) formData.append('nida', form.nida);
            if (form.tin) formData.append('tin', form.tin);
            if (form.photo) formData.append('photo', form.photo);
            formData.append('phone_verified', form.phone_verified);
            
            // New Fields
            formData.append('gender', form.gender);
            formData.append('religion', form.religion);
            formData.append('marital_status', form.marital_status);
            formData.append('short_name', form.short_name || '');
            formData.append('email_signature', form.email_signature || '');
            if (form.signature_image) formData.append('signature_image', form.signature_image);
            if (form.staff_sign) formData.append('staff_sign', form.staff_sign);

            contracts.forEach((contract, index) => {
                if (contract.id) formData.append(`contracts[${index}]id`, contract.id);
                formData.append(`contracts[${index}]start_date`, contract.start_date);
                if (contract.end_date) formData.append(`contracts[${index}]end_date`, contract.end_date);
                if (contract.gross_salary) formData.append(`contracts[${index}]gross_salary`, stripMoneyFormatting(contract.gross_salary));
                formData.append(`contracts[${index}]others`, stripMoneyFormatting(contract.others || '0.00'));
                formData.append(`contracts[${index}]allowance`, stripMoneyFormatting(contract.allowance || '0.00'));
                formData.append(`contracts[${index}]no_nssf`, contract.no_nssf ? 'true' : 'false');
                formData.append(`contracts[${index}]no_nhif`, contract.no_nhif ? 'true' : 'false');
                if (contract.document instanceof File) {
                    formData.append(`contracts[${index}]document`, contract.document);
                }
            });

            documents.forEach((doc, index) => {
                if (doc.id) formData.append(`documents[${index}]id`, doc.id);
                formData.append(`documents[${index}]name`, doc.name);
                if (doc.file instanceof File) {
                    formData.append(`documents[${index}]file`, doc.file);
                }
            });

            guarantors.forEach((g, index) => {
                if (g.id) formData.append(`guarantors[${index}]id`, g.id);
                formData.append(`guarantors[${index}]first_name`, g.first_name);
                formData.append(`guarantors[${index}]middle_name`, g.middle_name);
                formData.append(`guarantors[${index}]last_name`, g.last_name);
                formData.append(`guarantors[${index}]gender`, g.gender);
                formData.append(`guarantors[${index}]phone`, g.phone);
                formData.append(`guarantors[${index}]phone_verified`, g.phone_verified);
                formData.append(`guarantors[${index}]nida`, g.nida);
                formData.append(`guarantors[${index}]relationship`, g.relationship);
                formData.append(`guarantors[${index}]place_of_work`, g.place_of_work);
                if (g.photo instanceof File) {
                    formData.append(`guarantors[${index}]photo`, g.photo);
                }
                if (g.nida_copy instanceof File) {
                    formData.append(`guarantors[${index}]nida_copy`, g.nida_copy);
                }
                if (g.local_government_letter instanceof File) {
                    formData.append(`guarantors[${index}]local_government_letter`, g.local_government_letter);
                }
                if (g.guarantor_letter instanceof File) {
                    formData.append(`guarantors[${index}]guarantor_letter`, g.guarantor_letter);
                }
            });

            familyMembers.forEach((fm, index) => {
                if (fm.id) formData.append(`family_members[${index}]id`, fm.id);
                formData.append(`family_members[${index}]first_name`, fm.first_name);
                formData.append(`family_members[${index}]middle_name`, fm.middle_name);
                formData.append(`family_members[${index}]last_name`, fm.last_name);
                formData.append(`family_members[${index}]relation`, fm.relation);
                formData.append(`family_members[${index}]dob`, fm.dob);
                if (fm.phone) formData.append(`family_members[${index}]phone`, fm.phone);
                if (fm.address) formData.append(`family_members[${index}]address`, fm.address);
            });

            const url = form.id ? `${STAFF_API}${form.id}/` : STAFF_API;
            const method = form.id ? 'PATCH' : 'POST';
            
            const res = await fetch(url, {
                method,
                headers: authHeaders(false), 
                body: formData
            });

            if (res.ok) {
                toast.success(form.id ? 'Staff updated successfully' : 'Staff created successfully');
                onClose(true); 
            } else {
                const err = await res.json();
                toast.error('Failed to save: ' + (err.detail || JSON.stringify(err)));
            }
        } catch (error) {
            console.error(error);
            toast.error('An error occurred while saving.');
        } finally { 
            setSaving(false); 
        }
    };

    const deptOptions = departments.map(d => ({ id: d.id, text: d.name }));

    return (
        <div className="dialog-container" style={{ boxShadow: 'none', border: 'none', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div className="dialog-header">
                <h3>{form.id ? (readOnly ? 'View Staff' : 'Edit Staff') : 'New Staff'}</h3>
            </div>
            
            <div className="dialog-body" style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
                <form id="staff-form" onSubmit={handleSubmit}>
                    <div className="form-section">
                        <h4>Basic Information</h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                            <div style={{ textAlign: 'center', alignSelf: 'center' }}>
                                <div style={{ 
                                    width: '150px', height: '150px', border: '1px solid #ccc', 
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    marginBottom: '10px', overflow: 'hidden', background: '#f9f9f9'
                                }}>
                                    {form.photo_preview ? (
                                        <img src={form.photo_preview} alt="Staff" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                    ) : (
                                        <span style={{ color: '#ccc' }}>No Photo</span>
                                    )}
                                </div>
                                {!readOnly && (
                                    <input type="file" accept="image/*" onChange={handlePhotoChange} />
                                )}
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20 }}>
                                <div>
                                    <label className="required">First Name</label>
                                    <input
                                        value={form.first_name}
                                        onChange={e => handleInputChange(e, 'first_name')}
                                        readOnly={readOnly}
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="required">Middle Name</label>
                                    <input
                                        value={form.middle_name}
                                        onChange={e => handleInputChange(e, 'middle_name')}
                                        readOnly={readOnly}
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="required">Last Name</label>
                                    <input
                                        value={form.last_name}
                                        onChange={e => handleInputChange(e, 'last_name')}
                                        readOnly={readOnly}
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="required">Email</label>
                                    <input
                                        type="email"
                                        value={form.email}
                                        onChange={handleEmailChange}
                                        readOnly={readOnly}
                                        required
                                    />
                                </div>
                                <div>
                                    <label>Office Email</label>
                                    <input
                                        type="email"
                                        value={form.office_email}
                                        onChange={e => handleInputChange(e, 'office_email')}
                                        readOnly={readOnly}
                                    />
                                </div>
                                <div>
                                    <label className="required">Department</label>
                                    <Select2Dropdown
                                        options={[{ id: '', text: 'Select Department' }, ...deptOptions]}
                                        value={form.department}
                                        onChange={val => setForm(f => ({ ...f, department: val }))}
                                        valueKey="id"
                                        displayKey="text"
                                        isDisabled={readOnly}
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="required">Phone</label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                        <input
                                            value={form.phone}
                                            onChange={e => handleInputChange(e, 'phone')}
                                            readOnly={readOnly}
                                            style={{ flex: 1 }}
                                            required
                                        />
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.9em', cursor: readOnly ? 'default' : 'pointer' }}>
                                            <input
                                                type="checkbox"
                                                checked={form.phone_verified}
                                                onChange={e => setForm(f => ({ ...f, phone_verified: e.target.checked }))}
                                                disabled={readOnly}
                                            />
                                            Verified
                                        </label>
                                    </div>
                                </div>
                                <div>
                                    <label className="required">Position</label>
                                    <input
                                        value={form.position}
                                        onChange={e => handleInputChange(e, 'position')}
                                        readOnly={readOnly}
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="required">Address</label>
                                    <input
                                        value={form.address}
                                        onChange={e => handleInputChange(e, 'address')}
                                        readOnly={readOnly}
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="required">Date of Birth</label>
                                    <input
                                        type="date"
                                        value={form.dob}
                                        onChange={handleDobChange}
                                        readOnly={readOnly}
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="required">Joining Date</label>
                                    <input
                                        type="date"
                                        value={form.joining_date}
                                        onChange={e => setForm(f => ({ ...f, joining_date: e.target.value }))}
                                        readOnly={readOnly}
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="required">NIDA (yyyymmdd-xxxxx-xxxxx-xx)</label>
                                    <input
                                        value={form.nida}
                                        onChange={handleNidaChange}
                                        onBlur={() => checkNidaAvailability(form.nida, 'staff')}
                                        readOnly={readOnly}
                                        placeholder="yyyymmdd-xxxxx-xxxxx-xx"
                                        required
                                        style={nidaError ? { borderColor: 'red' } : {}}
                                    />
                                    {nidaError && <small style={{ color: 'red', display: 'block', marginTop: 4 }}>{nidaError}</small>}
                                </div>
                                <div>
                                    <label className="required">TIN</label>
                                    <input
                                        value={form.tin}
                                        onChange={handleTinChange}
                                        readOnly={readOnly}
                                        placeholder="xxx-xxx-xxx"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="required">NSSF</label>
                                    <input
                                        value={form.nssf}
                                        onChange={handleNssfChange}
                                        readOnly={readOnly}
                                        required
                                    />
                                </div>
                                
                                {/* New Fields */}
                                <div>
                                    <label className="required">Gender</label>
                                    <div style={{ display: 'flex', gap: 15, marginTop: 5 }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                            <input 
                                                type="radio" 
                                                name="gender" 
                                                value="MALE" 
                                                checked={form.gender === 'MALE'} 
                                                onChange={e => setForm(f => ({ ...f, gender: e.target.value }))}
                                                disabled={readOnly}
                                            />
                                            Male
                                        </label>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                            <input 
                                                type="radio" 
                                                name="gender" 
                                                value="FEMALE" 
                                                checked={form.gender === 'FEMALE'} 
                                                onChange={e => setForm(f => ({ ...f, gender: e.target.value }))}
                                                disabled={readOnly}
                                            />
                                            Female
                                        </label>
                                    </div>
                                </div>
                                <div>
                                    <label className="required">Religion</label>
                                    <Select2Dropdown
                                        options={RELIGION_OPTIONS}
                                        value={form.religion}
                                        onChange={val => setForm(f => ({ ...f, religion: val }))}
                                        valueKey="id"
                                        displayKey="text"
                                        isDisabled={readOnly}
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="required">Marital Status</label>
                                    <Select2Dropdown
                                        options={MARITAL_STATUS_OPTIONS}
                                        value={form.marital_status}
                                        onChange={val => setForm(f => ({ ...f, marital_status: val }))}
                                        valueKey="id"
                                        displayKey="text"
                                        isDisabled={readOnly}
                                        required
                                    />
                                </div>

                                <div>
                                    <label>Short Name</label>
                                    <input
                                        value={form.short_name}
                                        onChange={e => handleInputChange(e, 'short_name')}
                                        readOnly={readOnly}
                                        placeholder="e.g. JD"
                                    />
                                </div>

                                <div style={{ gridColumn: '1 / -1' }}>
                                    <label>Email Signature</label>
                                    <textarea
                                        value={form.email_signature}
                                        onChange={e => handleInputChange(e, 'email_signature')}
                                        readOnly={readOnly}
                                        rows={4}
                                        placeholder="Signature text or HTML..."
                                        style={{ width: '100%', fontFamily: 'inherit', fontSize: 13 }}
                                    />
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 6, flexWrap: 'wrap' }}>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={e => setForm(f => ({
                                                ...f,
                                                signature_image: e.target.files[0] || null,
                                                signature_image_preview: e.target.files[0] ? URL.createObjectURL(e.target.files[0]) : f.signature_image_preview
                                            }))}
                                            disabled={readOnly}
                                            style={{ fontSize: 12 }}
                                        />
                                        {form.signature_image_preview && (
                                            <a href={form.signature_image_preview} target="_blank" rel="noopener noreferrer">
                                                <img src={form.signature_image_preview} alt="Signature" style={{ maxHeight: 48, border: '1px solid #ddd', borderRadius: 4 }} />
                                            </a>
                                        )}
                                    </div>
                                </div>

                                <div style={{ gridColumn: '1 / -1' }}>
                                    <label>Staff Sign</label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 6, flexWrap: 'wrap' }}>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={e => setForm(f => ({
                                                ...f,
                                                staff_sign: e.target.files[0] || null,
                                                staff_sign_preview: e.target.files[0] ? URL.createObjectURL(e.target.files[0]) : f.staff_sign_preview
                                            }))}
                                            disabled={readOnly}
                                            style={{ fontSize: 12 }}
                                        />
                                        {form.staff_sign_preview && (
                                            <a href={form.staff_sign_preview} target="_blank" rel="noopener noreferrer">
                                                <img src={form.staff_sign_preview} alt="Staff Sign" style={{ maxHeight: 48, border: '1px solid #ddd', borderRadius: 4 }} />
                                            </a>
                                        )}
                                    </div>
                                </div>

                                <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: readOnly ? 'default' : 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={form.active}
                                            onChange={e => setForm(f => ({ ...f, active: e.target.checked }))}
                                            disabled={readOnly}
                                        />
                                        Active Staff Member
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>

                    <hr style={{ margin: '20px 0', border: '0', borderTop: '1px solid #eee' }} />

                    <div className="form-section">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                            <h4>Family Members (Max 10)</h4>
                            {!readOnly && (
                                <button type="button" className="btn-small btn-primary" onClick={addFamilyMember}>+ Add Family Member</button>
                            )}
                        </div>
                        {familyMembers.map((fm, idx) => {
                            const dob = new Date(fm.dob);
                            const today = new Date();
                            let age = today.getFullYear() - dob.getFullYear();
                            const m = today.getMonth() - dob.getMonth();
                            if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
                                age--;
                            }
                            const isUnder18 = fm.dob && age < 18;

                            return (
                            <div key={idx} style={{ background: '#f9f9f9', padding: 15, borderRadius: 4, marginBottom: 10, position: 'relative' }}>
                                {!readOnly && (
                                    <button 
                                        type="button" 
                                        onClick={() => removeFamilyMember(idx)}
                                        style={{ position: 'absolute', top: 5, right: 5, background: 'none', border: 'none', color: 'red', cursor: 'pointer' }}
                                    >
                                        &times;
                                    </button>
                                )}
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                                    <div>
                                        <label className="required">First Name</label>
                                        <input 
                                            value={fm.first_name} 
                                            onChange={e => updateFamilyMember(idx, 'first_name', e.target.value)}
                                            readOnly={readOnly}
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="required">Middle Name</label>
                                        <input 
                                            value={fm.middle_name} 
                                            onChange={e => updateFamilyMember(idx, 'middle_name', e.target.value)}
                                            readOnly={readOnly}
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="required">Last Name</label>
                                        <input 
                                            value={fm.last_name} 
                                            onChange={e => updateFamilyMember(idx, 'last_name', e.target.value)}
                                            readOnly={readOnly}
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="required">Relation</label>
                                        <Select2Dropdown
                                            options={FAMILY_RELATION_OPTIONS}
                                            value={fm.relation}
                                            onChange={val => updateFamilyMember(idx, 'relation', val)}
                                            valueKey="id"
                                            displayKey="text"
                                            isDisabled={readOnly}
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="required">Date of Birth</label>
                                        <input 
                                            type="date"
                                            value={fm.dob} 
                                            onChange={e => updateFamilyMember(idx, 'dob', e.target.value)}
                                            readOnly={readOnly}
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className={!isUnder18 || !fm.phone_na ? "required" : ""}>Phone</label>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                            <input 
                                                value={fm.phone} 
                                                onChange={e => updateFamilyMember(idx, 'phone', e.target.value)}
                                                readOnly={readOnly || (isUnder18 && fm.phone_na)}
                                                disabled={isUnder18 && fm.phone_na}
                                                style={{ flex: 1, background: (isUnder18 && fm.phone_na) ? '#eee' : 'white' }}
                                            />
                                            {isUnder18 && (
                                                <label style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: '0.8em', whiteSpace: 'nowrap' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={fm.phone_na || false}
                                                        onChange={e => {
                                                            updateFamilyMember(idx, 'phone_na', e.target.checked);
                                                            if (e.target.checked) updateFamilyMember(idx, 'phone', '');
                                                        }}
                                                        disabled={readOnly}
                                                    />
                                                    N/A
                                                </label>
                                            )}
                                        </div>
                                    </div>
                                    <div style={{ gridColumn: 'span 3' }}>
                                        <label>Address</label>
                                        <input 
                                            value={fm.address} 
                                            onChange={e => updateFamilyMember(idx, 'address', e.target.value)}
                                            readOnly={readOnly}
                                        />
                                    </div>
                                </div>
                            </div>
                        )})}
                        {familyMembers.length === 0 && <p style={{ color: '#888', fontStyle: 'italic' }}>No family members added.</p>}
                    </div>

                    <hr style={{ margin: '20px 0', border: '0', borderTop: '1px solid #eee' }} />

                    <div className="form-section">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                            <h4>Contracts</h4>
                            {!readOnly && (
                                <button type="button" className="btn-small btn-primary" onClick={addContract}>+ Add Contract</button>
                            )}
                        </div>
                        {sortedContractsWithIndex.map(({ contract, originalIndex }) => (
                            <div key={contract.id || originalIndex} style={{ background: '#f9f9f9', padding: 15, borderRadius: 4, marginBottom: 10, position: 'relative' }}>
                                {!readOnly && (
                                    <button 
                                        type="button" 
                                        onClick={() => removeContract(originalIndex)}
                                        style={{ position: 'absolute', top: 5, right: 5, background: 'none', border: 'none', color: 'red', cursor: 'pointer' }}
                                    >
                                        &times;
                                    </button>
                                )}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '110px 110px repeat(3, minmax(120px, 1fr))', gap: 8, alignItems: 'end' }}>
                                        <div>
                                        <label className="required">Start Date</label>
                                        <input 
                                            type="date" 
                                            value={contract.start_date} 
                                            onChange={e => updateContract(originalIndex, 'start_date', e.target.value)}
                                            readOnly={readOnly}
                                            style={{ fontSize: 12, padding: '6px 8px', height: 34 }}
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="required">End Date</label>
                                        <input 
                                            type="date" 
                                            value={contract.end_date} 
                                            onChange={e => updateContract(originalIndex, 'end_date', e.target.value)}
                                            readOnly={readOnly}
                                            style={{ fontSize: 12, padding: '6px 8px', height: 34 }}
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="required">Gross Salary</label>
                                        <input 
                                            type="text"
                                            value={contract.gross_salary || ''}
                                            onChange={e => handleContractMoneyChange(originalIndex, 'gross_salary', e.target.value)}
                                            onFocus={e => handleContractMoneyFocus(originalIndex, 'gross_salary', e.target.value)}
                                            onBlur={e => handleContractMoneyBlur(originalIndex, 'gross_salary', e.target.value)}
                                            readOnly={readOnly}
                                            style={{ fontSize: 12, padding: '6px 8px', height: 34 }}
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label>Others</label>
                                        <input
                                            type="text"
                                            value={contract.others || ''}
                                            onChange={e => handleContractMoneyChange(originalIndex, 'others', e.target.value)}
                                            onFocus={e => handleContractMoneyFocus(originalIndex, 'others', e.target.value)}
                                            onBlur={e => handleContractMoneyBlur(originalIndex, 'others', e.target.value)}
                                            readOnly={readOnly}
                                            style={{ fontSize: 12, padding: '6px 8px', height: 34 }}
                                        />
                                    </div>
                                    <div>
                                        <label>Allowance</label>
                                        <input
                                            type="text"
                                            value={contract.allowance || ''}
                                            onChange={e => handleContractMoneyChange(originalIndex, 'allowance', e.target.value)}
                                            onFocus={e => handleContractMoneyFocus(originalIndex, 'allowance', e.target.value)}
                                            onBlur={e => handleContractMoneyBlur(originalIndex, 'allowance', e.target.value)}
                                            readOnly={readOnly}
                                            style={{ fontSize: 12, padding: '6px 8px', height: 34 }}
                                        />
                                    </div>
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12, alignItems: 'end' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 20, minHeight: 34 }}>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 'normal', cursor: readOnly ? 'default' : 'pointer', margin: 0 }}>
                                                <input
                                                    type="checkbox"
                                                    checked={!!contract.no_nssf}
                                                    onChange={e => !readOnly && updateContract(originalIndex, 'no_nssf', e.target.checked)}
                                                    disabled={readOnly}
                                                />
                                                NO NSSF
                                            </label>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 'normal', cursor: readOnly ? 'default' : 'pointer', margin: 0 }}>
                                                <input
                                                    type="checkbox"
                                                    checked={!!contract.no_nhif}
                                                    onChange={e => !readOnly && updateContract(originalIndex, 'no_nhif', e.target.checked)}
                                                    disabled={readOnly}
                                                />
                                                NO NHIF
                                            </label>
                                        </div>
                                        <div>
                                            <label className="required">Add Document</label>
                                            {contract.document ? (
                                                <div style={{ marginBottom: 5 }}>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleViewFile(contract.document, 'Contract Document')}
                                                        style={{ fontSize: 12, background: '#007bff', color: '#fff', border: 'none', borderRadius: 4, padding: '2px 8px', cursor: 'pointer' }}
                                                        title="View File"
                                                    >
                                                        View File
                                                    </button>
                                                    {readOnly && <div style={{ fontSize: '0.8em', color: '#666', marginTop: 2 }}>{getFileName(contract.document)}</div>}
                                                </div>
                                            ) : null}
                                            {!readOnly && (
                                                <div>
                                                    <input type="file" onChange={e => handleContractFileChange(originalIndex, e)} />
                                                    {contract.document && <div style={{ fontSize: '0.8em', color: '#666', marginTop: 2 }}>Current: {getFileName(contract.document)}</div>}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                        {contracts.length === 0 && <p style={{ color: '#888', fontStyle: 'italic' }}>No contracts added.</p>}
                    </div>

                    <hr style={{ margin: '20px 0', border: '0', borderTop: '1px solid #eee' }} />

                    <div className="form-section">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                            <h4>Guarantors (Min. 2)</h4>
                            {!readOnly && (
                                <button type="button" className="btn-small btn-primary" onClick={addGuarantor}>+ Add Guarantor</button>
                            )}
                        </div>
                        {guarantors.map((g, idx) => (
                            <div key={idx} style={{ background: '#f9f9f9', padding: 15, borderRadius: 4, marginBottom: 10, position: 'relative' }}>
                                {!readOnly && (
                                    <button 
                                        type="button" 
                                        onClick={() => removeGuarantor(idx)}
                                        style={{ position: 'absolute', top: 5, right: 5, background: 'none', border: 'none', color: 'red', cursor: 'pointer' }}
                                    >
                                        &times;
                                    </button>
                                )}
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                                    <div>
                                        <label className="required">First Name</label>
                                        <input 
                                            value={g.first_name} 
                                            onChange={e => updateGuarantor(idx, 'first_name', e.target.value)}
                                            readOnly={readOnly}
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="required">Middle Name</label>
                                        <input 
                                            value={g.middle_name} 
                                            onChange={e => updateGuarantor(idx, 'middle_name', e.target.value)}
                                            readOnly={readOnly}
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="required">Last Name</label>
                                        <input 
                                            value={g.last_name} 
                                            onChange={e => updateGuarantor(idx, 'last_name', e.target.value)}
                                            readOnly={readOnly}
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="required">Gender</label>
                                        <div style={{ display: 'flex', gap: 15, marginTop: 5 }}>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                                <input 
                                                    type="radio" 
                                                    name={`guarantor_gender_${idx}`}
                                                    value="MALE" 
                                                    checked={g.gender === 'MALE'} 
                                                    onChange={e => updateGuarantor(idx, 'gender', e.target.value)}
                                                    disabled={readOnly}
                                                />
                                                Male
                                            </label>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                                <input 
                                                    type="radio" 
                                                    name={`guarantor_gender_${idx}`}
                                                    value="FEMALE" 
                                                    checked={g.gender === 'FEMALE'} 
                                                    onChange={e => updateGuarantor(idx, 'gender', e.target.value)}
                                                    disabled={readOnly}
                                                />
                                                Female
                                            </label>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="required">Phone</label>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                            <input 
                                                value={g.phone} 
                                                onChange={e => updateGuarantor(idx, 'phone', e.target.value)}
                                                readOnly={readOnly}
                                                required
                                                style={{ flex: 1 }}
                                            />
                                            <label style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: '0.8em' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={g.phone_verified}
                                                    onChange={e => updateGuarantor(idx, 'phone_verified', e.target.checked)}
                                                    disabled={readOnly}
                                                />
                                                Verified
                                            </label>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="required">NIDA</label>
                                        <input 
                                            value={g.nida} 
                                            onChange={e => updateGuarantor(idx, 'nida', e.target.value)}
                                            onBlur={() => checkNidaAvailability(g.nida, 'guarantor', idx)}
                                            readOnly={readOnly}
                                            placeholder="yyyymmdd-xxxxx-xxxxx-xx"
                                            required
                                            style={g.nida_error ? { borderColor: 'red' } : {}}
                                        />
                                        {g.nida_error && <small style={{ color: 'red', display: 'block', marginTop: 4 }}>{g.nida_error}</small>}
                                    </div>
                                    <div>
                                        <label className="required">Relationship</label>
                                        <input 
                                            value={g.relationship} 
                                            onChange={e => updateGuarantor(idx, 'relationship', e.target.value)}
                                            readOnly={readOnly}
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="required">Place of Work</label>
                                        <input 
                                            value={g.place_of_work} 
                                            onChange={e => updateGuarantor(idx, 'place_of_work', e.target.value)}
                                            readOnly={readOnly}
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="required">Photo</label>
                                        {g.photo ? (
                                            <div style={{ marginBottom: 5 }}>
                                                <button
                                                    type="button"
                                                    onClick={() => handleViewFile(g.photo, 'Guarantor Photo')}
                                                    style={{ fontSize: 12, background: '#007bff', color: '#fff', border: 'none', borderRadius: 4, padding: '2px 8px', cursor: 'pointer' }}
                                                    title="View Photo"
                                                >
                                                    View Photo
                                                </button>
                                                {readOnly && <div style={{ fontSize: '0.8em', color: '#666', marginTop: 2 }}>{getFileName(g.photo)}</div>}
                                            </div>
                                        ) : null}
                                        {!readOnly && (
                                            <div>
                                                <input type="file" accept="image/*" onChange={e => handleGuarantorFileChange(idx, 'photo', e)} />
                                                {g.photo && <div style={{ fontSize: '0.8em', color: '#666', marginTop: 2 }}>Current: {getFileName(g.photo)}</div>}
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <label className="required">NIDA Copy</label>
                                        {g.nida_copy ? (
                                            <div style={{ marginBottom: 5 }}>
                                                <button
                                                    type="button"
                                                    onClick={() => handleViewFile(g.nida_copy, 'Guarantor NIDA')}
                                                    style={{ fontSize: 12, background: '#007bff', color: '#fff', border: 'none', borderRadius: 4, padding: '2px 8px', cursor: 'pointer' }}
                                                    title="View NIDA"
                                                >
                                                    View NIDA
                                                </button>
                                                {readOnly && <div style={{ fontSize: '0.8em', color: '#666', marginTop: 2 }}>{getFileName(g.nida_copy)}</div>}
                                            </div>
                                        ) : null}
                                        {!readOnly && (
                                            <div>
                                                <input type="file" onChange={e => handleGuarantorFileChange(idx, 'nida_copy', e)} />
                                                {g.nida_copy && <div style={{ fontSize: '0.8em', color: '#666', marginTop: 2 }}>Current: {getFileName(g.nida_copy)}</div>}
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <label className="required">Local Gov. Letter</label>
                                        {g.local_government_letter ? (
                                            <div style={{ marginBottom: 5 }}>
                                                <button
                                                    type="button"
                                                    onClick={() => handleViewFile(g.local_government_letter, 'Local Gov. Letter')}
                                                    style={{ fontSize: 12, background: '#007bff', color: '#fff', border: 'none', borderRadius: 4, padding: '2px 8px', cursor: 'pointer' }}
                                                    title="View Letter"
                                                >
                                                    View Letter
                                                </button>
                                                {readOnly && <div style={{ fontSize: '0.8em', color: '#666', marginTop: 2 }}>{getFileName(g.local_government_letter)}</div>}
                                            </div>
                                        ) : null}
                                        {!readOnly && (
                                            <div>
                                                <input type="file" onChange={e => handleGuarantorFileChange(idx, 'local_government_letter', e)} />
                                                {g.local_government_letter && <div style={{ fontSize: '0.8em', color: '#666', marginTop: 2 }}>Current: {getFileName(g.local_government_letter)}</div>}
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <label className="required">Guarantor Letter</label>
                                        {g.guarantor_letter ? (
                                            <div style={{ marginBottom: 5 }}>
                                                <button
                                                    type="button"
                                                    onClick={() => handleViewFile(g.guarantor_letter, 'Guarantor Letter')}
                                                    style={{ fontSize: 12, background: '#007bff', color: '#fff', border: 'none', borderRadius: 4, padding: '2px 8px', cursor: 'pointer' }}
                                                    title="View Letter"
                                                >
                                                    View Letter
                                                </button>
                                                {readOnly && <div style={{ fontSize: '0.8em', color: '#666', marginTop: 2 }}>{getFileName(g.guarantor_letter)}</div>}
                                            </div>
                                        ) : null}
                                        {!readOnly && (
                                            <div>
                                                <input type="file" onChange={e => handleGuarantorFileChange(idx, 'guarantor_letter', e)} />
                                                {g.guarantor_letter && <div style={{ fontSize: '0.8em', color: '#666', marginTop: 2 }}>Current: {getFileName(g.guarantor_letter)}</div>}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                        {guarantors.length === 0 && <p style={{ color: '#888', fontStyle: 'italic' }}>No guarantors added.</p>}
                    </div>

                    <hr style={{ margin: '20px 0', border: '0', borderTop: '1px solid #eee' }} />

                    <div className="form-section">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                            <h4>Documents</h4>
                            {!readOnly && (
                                <button type="button" className="btn-small btn-primary" onClick={addDocument}>+ Add Document</button>
                            )}
                        </div>
                        {documents.map((doc, idx) => (
                            <div key={idx} style={{ background: '#f9f9f9', padding: 15, borderRadius: 4, marginBottom: 10, position: 'relative' }}>
                                {!readOnly && (
                                    <button 
                                        type="button" 
                                        onClick={() => removeDocument(idx)}
                                        style={{ position: 'absolute', top: 5, right: 5, background: 'none', border: 'none', color: 'red', cursor: 'pointer' }}
                                    >
                                        &times;
                                    </button>
                                )}
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                                    <div>
                                        <label className="required">Document Name</label>
                                        <input 
                                            value={doc.name} 
                                            onChange={e => updateDocument(idx, 'name', e.target.value)}
                                            readOnly={readOnly}
                                            placeholder="e.g. CV, ID Copy"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="required">File</label>
                                        {doc.file ? (
                                            <div style={{ marginBottom: 5 }}>
                                                <button
                                                    type="button"
                                                    onClick={() => handleViewFile(doc.file, doc.name)}
                                                    style={{ fontSize: 12, background: '#007bff', color: '#fff', border: 'none', borderRadius: 4, padding: '2px 8px', cursor: 'pointer' }}
                                                    title="View File"
                                                >
                                                    View File
                                                </button>
                                                {readOnly && <div style={{ fontSize: '0.8em', color: '#666', marginTop: 2 }}>{getFileName(doc.file)}</div>}
                                            </div>
                                        ) : null}
                                        {!readOnly && (
                                            <div>
                                                <input type="file" onChange={e => handleDocumentFileChange(idx, e)} />
                                                {doc.file && <div style={{ fontSize: '0.8em', color: '#666', marginTop: 2 }}>Current: {getFileName(doc.file)}</div>}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                        {documents.length === 0 && <p style={{ color: '#888', fontStyle: 'italic' }}>No documents added.</p>}
                    </div>

                </form>
            </div>

            <div className="dialog-footer">
                {readOnly && hasPermission('STAFFS', 'can_edit') && (
                    <button type="button" className="dialog-btn dialog-btn-primary" onClick={() => setReadOnly(false)}>Edit</button>
                )}
                
                {!readOnly && (
                    <button 
                        type="submit" 
                        form="staff-form"
                        className="dialog-btn dialog-btn-primary"
                        disabled={saving}
                    >
                        {saving ? 'Saving...' : 'Save'}
                    </button>
                )}
                
                <button 
                    type="button" 
                    className="dialog-btn dialog-btn-secondary"
                    onClick={() => onClose(false)}
                >
                    Close
                </button>
            </div>
        </div>
    );
};

export default StaffForm;